import { AddressFlow, EnergyType } from '@energy-retailer/constants';
import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';

import { getAddressFlow } from '@/app/lib/utils/get-address-flow';
import { getAddressDetails } from '@/app/queries';
import { MODAL_NAMES } from '@/constants';

import { CartSlice, type CartSliceActions, type CartSliceState } from './cart'
import { CheckboxSlice, type CheckboxSliceActions, type CheckboxSliceState } from './checkbox';
import { FlowSlice, type FlowSliceActions, type FlowSliceState } from './flow';
import { ModalSlice, type ModalSliceState, type ModalSliceActions } from './modals';
import { PromotionSlice, type PromotionSliceActions, type PromotionSliceState } from './promotion';
import { PropertySlice, type PropertySliceActions, type PropertySliceState } from './property';
import { StepsSlice, type StepsSliceState, type StepsSliceActions } from './steps';

import type { DataType } from '@/app/components/typeahead/typeahead.types';

type SharedSliceType = {
  selectAddress: (address: DataType) => Promise<void>;
  setCheckboxChecked: (fuel: EnergyType, checked: boolean) => void;
  resetJoinFormStore: () => void;
};

type JoinFormStore = StepsSliceState
  & StepsSliceActions
  & CheckboxSliceActions
  & CheckboxSliceState
  & PropertySliceActions
  & PropertySliceState
  & SharedSliceType
  & ModalSliceState
  & ModalSliceActions
  & CartSliceState
  & CartSliceActions
  & PromotionSliceState
  & PromotionSliceActions
  & FlowSliceState
  & FlowSliceActions;

const useJoinFormStore = create<JoinFormStore>()(
  persist(devtools(
    (set, get, store) => ({
      ...StepsSlice(set, get, store),
      ...CheckboxSlice(set, get, store),
      ...PropertySlice(set, get, store),
      ...ModalSlice(set, get, store),
      ...CartSlice(set, get, store),
      ...PromotionSlice(set, get, store),
      ...FlowSlice(set, get, store),
      resetJoinFormStore: () => {
        get().resetCheckbox();
        get().resetModals();
        get().resetSteps();
        get().resetProperty();
        get().resetCart();
        get().resetPromotion();
        get().resetFlow();
      },
      setCheckboxChecked: (fuel: EnergyType, checked: boolean) => {
        // set checkbox state
        get().setChecked(fuel, checked);
        // add or remove steps
        if (!checked) {
          get().removeStep(fuel);

          if (fuel === EnergyType.Electricity) {
            get().setElectricityPlanId(null);
            get().setElectricityPlanSlug(null);
            get().setFluxId({ productId: null });
          } else {
            get().setGasPlanId(null);
            get().setGasPlanSlug(null);
            get().setGasFluxId({ productId: null });
          }
        } else {
          get().addStep(fuel);
        }

        get().setFlow();
      },
      selectAddress: async (address: DataType) => {
        get().setPropertyAddressLookupDetails(address.id, address.name);

        const addressData = await getAddressDetails(address.id);

        // Open NMI search modal if no address data is returned
        if (addressData.length === 0) {
          get().openModal(MODAL_NAMES.NmiSearch);
          return;
        }
        
        // TODO:  remove getaddressflow and rewrite it here, edge cases galore
        
        const addressFlow = getAddressFlow({ data: addressData });
        const firstAddress = addressData[0];

        switch (addressFlow) {
          case AddressFlow.AddressNotFoundRequestNMI:
            // Incase getAddressFlow is invoked from elsewhere.
            // Open NMI search modal if no address data is returned
            get().openModal(MODAL_NAMES.NmiSearch);
            break;
          case AddressFlow.OutOfService:
            get().setPropertyDetails(firstAddress);
            get().openModal(MODAL_NAMES.OutsideServiceArea);
            break;
          case AddressFlow.AddressNotFound:
            get().setPropertyDetails(firstAddress);
            if (!firstAddress.isGasAvailable) {
              get().setAvailable(EnergyType.Gas, false);
              get().removeStep(EnergyType.Gas);
            }
            get().openModal(MODAL_NAMES.AddressNotFound);
            break;
          case AddressFlow.MultiNoGas:
          case AddressFlow.Multi:
            get().openModal(MODAL_NAMES.MultipleAddress);
            break;
          case AddressFlow.SingleNoGas:
            // trackAnalyticsEvent(ANALYTICS_EVENTS.hero.postCodeSearch, {
            //  newPostcode: firstAddress.address.postcode,
            // });
            get().setPropertyDetails(firstAddress);
            get().setAvailable(EnergyType.Gas, false);
            get().removeStep(EnergyType.Gas);
            break;
          case AddressFlow.Single:
          default:
            get().setPropertyDetails(firstAddress);
            break;
        }
      }
    }),
    {
      enabled: (
        process.env.ENV !== 'prod' &&
        process.env.NODE_ENV !== 'test' // disable in tests, `ENV` is undefined in unit tests
      )
    }
  ),
    {
      name: 'join-form-store',
      storage: createJSONStorage(() => sessionStorage),
      // filters out modals from the persisted state
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !['modals'].includes(key)),
        ),
    },
  )
);

export { useJoinFormStore };
